import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, styled } from '@mui/material';
import { TruckDataGridProps, TruckData } from '../types/ESALTypes';
import { ESALFormulas } from '../ESALFormulas';
import EsalNumberCell from './EsalNumberCell';

// Styled components for table styling
const StyledTableContainer = styled(TableContainer)(() => ({
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: 500,
    width: '100%',
}));

const StyledTableHead = styled(TableHead)(() => ({
    backgroundColor: '#f5f5f5',
    '& .MuiTableCell-head': {
        fontWeight: 'bold',
        fontSize: '0.875rem',
        padding: '12px 8px',
        borderRight: '1px solid #e0e0e0',
        '&:last-child': {
            borderRight: 'none',
        },
    },
}));

const StyledTableRow = styled(TableRow)(() => ({
    '&:hover': {
        backgroundColor: '#f9f9f9',
    },
    '& .MuiTableCell-body': {
        padding: '8px',
        borderRight: '1px solid #e0e0e0',
        fontSize: '0.875rem',
        '&:last-child': {
            borderRight: 'none',
        },
    },
}));

const EditableTableCell = styled(TableCell)(() => ({
    backgroundColor: '#fff3e0',
    '& .MuiTextField-root': {
        '& input': {
            padding: '4px 8px',
            fontSize: '0.875rem',
        },
    },
}));

const ResultTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    color: theme.palette.success.main,
}));

/**
 * Truck data grid component with inline editing capabilities
 * Shows both directions in the same table for each truck
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TruckDataGrid: React.FC<TruckDataGridProps> = ({ trucks, onTruckUpdate, isEditable, projectData, onValidityChange, resetKey }) => {
    const { t } = useTranslation();

    /**
     * Calculate truck ESAL with appropriate method based on available data
     * IMPORTANT: Always use AASHTO standard SN to match Excel calculations exactly
     */
    const calculateTruckESAL = useCallback(
        (truck: TruckData): TruckData => {
            // Always use AASHTO standard SN to match Excel reference calculations exactly
            // This ensures the grid shows the same values as the Excel sheet (Q10=592.99, Q14=336.86, etc.)
            return ESALFormulas.updateTruckESAL(truck, true, ESALFormulas.CONSTANTS.STRUCTURAL_NUMBER); // useDynamic=true, structuralNumber=5.0
        },
        [] // Remove projectData dependency to ensure consistent calculation
    );

    /**
     * Handle checkbox selection changes
     */
    const handleSelectionChange = useCallback(
        (truckId: number, selected: boolean) => {
            const truck = trucks.find(t => t.id === truckId);
            if (truck) {
                const updatedTruck = { ...truck, selected };
                // Recalculate ESAL when selection changes
                const recalculatedTruck = calculateTruckESAL(updatedTruck);
                onTruckUpdate(recalculatedTruck);
            }
        },
        [trucks, onTruckUpdate, calculateTruckESAL]
    );

    /**
     * Handle cell value changes with real-time calculation updates
     */
    const handleCellValueChange = useCallback(
        (truckId: number, field: string, numericValue: number) => {
            const truck = trucks.find(t => t.id === truckId);
            if (!truck) return;

            // Update the truck data
            const updatedTruck = { ...truck, [field]: numericValue };

            // Recalculate ESAL with updated values
            const recalculatedTruck = calculateTruckESAL(updatedTruck);
            onTruckUpdate(recalculatedTruck);
        },
        [trucks, onTruckUpdate, calculateTruckESAL]
    );

    /**
     * Calculate total ESAL for all selected trucks
     */
    const totalESAL = useMemo(() => {
        return trucks.filter(truck => truck.selected).reduce((total, truck) => total + truck.esalPerDay, 0);
    }, [trucks]);

    return (
        <Box sx={{ mb: 3 }}>
            {/* Table header with total */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    {t('esal_truck_data_title', 'Truck Traffic Data')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    {t('esal_total_esal', 'Total ESAL')}: {ESALFormulas.formatNumber(totalESAL, 3)} ESAL/day
                </Typography>
            </Box>

            {/* Table */}
            <Paper sx={{ overflow: 'hidden' }}>
                <StyledTableContainer>
                    <Table stickyHeader>
                        <StyledTableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ minWidth: 80, width: '7%' }}>
                                    {t('esal_table_select', 'Select')}
                                </TableCell>
                                <TableCell align="center" sx={{ minWidth: 120, width: '10%' }}>
                                    {t('esal_table_truck', 'Truck')}
                                </TableCell>
                                <TableCell align="center" sx={{ minWidth: 80, width: '7%' }}>
                                    {t('esal_table_axles', 'Axles')}
                                </TableCell>
                                <TableCell align="center" sx={{ minWidth: 140, width: '12%' }}>
                                    {t('esal_table_design_load', 'Design Load (kN)')}
                                </TableCell>
                                <TableCell align="center" sx={{ minWidth: 140, width: '12%' }}>
                                    {t('esal_table_custom_load', 'Custom Load (kN)')}
                                </TableCell>
                                <TableCell align="center" sx={{ minWidth: 160, width: '15%' }}>
                                    {t('esal_table_direction1', 'Direction 1 (vehicles/day)')}
                                </TableCell>
                                <TableCell align="center" sx={{ minWidth: 160, width: '15%' }}>
                                    {t('esal_table_direction2', 'Direction 2 (vehicles/day)')}
                                </TableCell>
                                <TableCell align="center" sx={{ minWidth: 130, width: '12%' }}>
                                    {t('esal_table_esal_per_day', 'ESAL/day')}
                                </TableCell>
                            </TableRow>
                        </StyledTableHead>
                        <TableBody>
                            {trucks.map(truck => (
                                <StyledTableRow key={truck.id}>
                                    {/* Selection checkbox */}
                                    <TableCell align="center">
                                        <Checkbox checked={truck.selected} onChange={e => handleSelectionChange(truck.id, e.target.checked)} size="small" disabled={!isEditable} />
                                    </TableCell>

                                    {/* Truck image */}
                                    <TableCell align="center">
                                        <img src={truck.imageSrc.default} alt={truck.name} style={{ height: 32, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
                                    </TableCell>

                                    {/* Number of axles (read-only) */}
                                    <TableCell align="center">
                                        <Typography variant="body2">{truck.axles}</Typography>
                                    </TableCell>

                                    {/* Design total load (read-only) */}
                                    <TableCell align="center">
                                        <Typography variant="body2">{ESALFormulas.formatNumber(truck.designTotalLoad, 0)}</Typography>
                                    </TableCell>

                                    {/* Custom total load (editable) */}
                                    <EditableTableCell align="center">
                                        <EsalNumberCell
                                            cellKey={`${truck.id}:customTotalLoad`}
                                            fieldLabel={t('esal_table_custom_load', 'Custom Load (kN)')}
                                            value={truck.customTotalLoad}
                                            onValueChange={value => handleCellValueChange(truck.id, 'customTotalLoad', value)}
                                            onValidityChange={onValidityChange}
                                            resetKey={resetKey}
                                            disabled={!isEditable || !truck.selected}
                                            inputProps={{ style: { textAlign: 'center' } }}
                                            placeholder={truck.designTotalLoad.toString()}
                                        />
                                    </EditableTableCell>

                                    {/* Direction 1 vehicles (editable) */}
                                    <EditableTableCell align="center">
                                        <EsalNumberCell
                                            cellKey={`${truck.id}:direction1Vehicles`}
                                            fieldLabel={t('esal_table_direction1', 'Direction 1 (vehicles/day)')}
                                            value={truck.direction1Vehicles}
                                            onValueChange={value => handleCellValueChange(truck.id, 'direction1Vehicles', value)}
                                            onValidityChange={onValidityChange}
                                            resetKey={resetKey}
                                            disabled={!isEditable || !truck.selected}
                                            inputProps={{ style: { textAlign: 'center' } }}
                                        />
                                    </EditableTableCell>

                                    {/* Direction 2 vehicles (editable) */}
                                    <EditableTableCell align="center">
                                        <EsalNumberCell
                                            cellKey={`${truck.id}:direction2Vehicles`}
                                            fieldLabel={t('esal_table_direction2', 'Direction 2 (vehicles/day)')}
                                            value={truck.direction2Vehicles}
                                            onValueChange={value => handleCellValueChange(truck.id, 'direction2Vehicles', value)}
                                            onValidityChange={onValidityChange}
                                            resetKey={resetKey}
                                            disabled={!isEditable || !truck.selected}
                                            inputProps={{ style: { textAlign: 'center' } }}
                                        />
                                    </EditableTableCell>

                                    {/* ESAL per day (calculated) */}
                                    <ResultTableCell align="center">
                                        <Typography variant="body2" fontWeight="bold">
                                            {truck.selected ? ESALFormulas.formatNumber(truck.esalPerDay, 3) : '0.000'}
                                        </Typography>
                                    </ResultTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            </Paper>
        </Box>
    );
};

export default TruckDataGrid;
